/* Schnauze Theme – global.js (ES module, kein jQuery, keine Frameworks) */

/* ---------- Helpers ---------- */
export function debounce(fn, wait = 250) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

export function formatMoney(cents, format = window.themeSettings?.moneyFormat || '€{{amount_with_comma_separator}}') {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const value = (cents / 100);
  function pad(num, precision, thousands, decimal) {
    const parts = num.toFixed(precision).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  }
  let formatted = '0';
  const match = format.match(placeholderRegex);
  switch (match ? match[1] : 'amount') {
    case 'amount': formatted = pad(value, 2, ',', '.'); break;
    case 'amount_no_decimals': formatted = pad(value, 0, ',', '.'); break;
    case 'amount_with_comma_separator': formatted = pad(value, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': formatted = pad(value, 0, '.', ','); break;
    default: formatted = pad(value, 2, '.', ',');
  }
  return format.replace(placeholderRegex, formatted);
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export function trapFocus(container, initial) {
  const elements = Array.from(container.querySelectorAll(FOCUSABLE));
  if (!elements.length) return () => {};
  const first = elements[0];
  const last = elements[elements.length - 1];
  (initial || first).focus({ preventScroll: true });
  function onKeydown(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
  container.addEventListener('keydown', onKeydown);
  return () => container.removeEventListener('keydown', onKeydown);
}

export function showToast(message) {
  let toast = document.querySelector('.toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'toast';
    toast.setAttribute('role', 'status');
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  requestAnimationFrame(() => toast.classList.add('is-visible'));
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 3200);
}

export function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
  };
}

/* Helpers global verfügbar machen – andere Asset-Module (facets.js, product.js, quiz.js)
   greifen über window zu, damit global.js nie doppelt instanziiert wird. */
window.themeHelpers = { debounce, formatMoney, trapFocus, showToast, fetchConfig };

/* ---------- Cart API (Section Rendering) ---------- */
export const CartAPI = {
  async add(body) {
    body.sections = this.sectionIds();
    body.sections_url = window.location.pathname;
    const response = await fetch(`${window.routes.cart_add_url}.js`, {
      ...fetchConfig(),
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || window.themeStrings.cartError);
    await this.refreshCount();
    this.renderSections(data.sections);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    return data;
  },
  async change(body) {
    body.sections = this.sectionIds();
    body.sections_url = window.location.pathname;
    const response = await fetch(`${window.routes.cart_change_url}.js`, {
      ...fetchConfig(),
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || window.themeStrings.cartError);
    this.updateCount(data.item_count);
    this.renderSections(data.sections);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    return data;
  },
  sectionIds() {
    const ids = [];
    if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
    const mainCart = document.querySelector('[data-main-cart-section]');
    if (mainCart) ids.push(mainCart.dataset.mainCartSection);
    return ids;
  },
  renderSections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) {
      const target = document.querySelector('#CartDrawer [data-cart-drawer-inner]');
      const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const source = parsed.querySelector('[data-cart-drawer-inner]');
      if (target && source) target.replaceWith(source);
    }
    const mainCart = document.querySelector('[data-main-cart-section]');
    if (mainCart && sections[mainCart.dataset.mainCartSection]) {
      const parsed = new DOMParser().parseFromString(sections[mainCart.dataset.mainCartSection], 'text/html');
      const source = parsed.querySelector('[data-main-cart-inner]');
      const target = mainCart.querySelector('[data-main-cart-inner]');
      if (target && source) target.replaceWith(source);
    }
  },
  async refreshCount() {
    const response = await fetch(`${window.routes.cart_url}.js`);
    const cart = await response.json();
    this.updateCount(cart.item_count);
    return cart;
  },
  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count > 0 ? count : '';
      el.toggleAttribute('data-empty', count === 0);
    });
  }
};
window.CartAPI = CartAPI;

/* ---------- Drawer (generisch: Menü + Warenkorb) ---------- */
class DrawerDialog extends HTMLElement {
  connectedCallback() {
    this.releaseFocus = null;
    this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
      btn.addEventListener('click', () => this.close())
    );
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }
  open(opener) {
    this.opener = opener || document.activeElement;
    this.classList.add('is-open');
    this.removeAttribute('hidden');
    document.body.classList.add('drawer-open');
    const panel = this.querySelector('.drawer__panel');
    this.releaseFocus = trapFocus(panel, panel.querySelector('[data-drawer-close]'));
    document.dispatchEvent(new CustomEvent('drawer:open', { detail: { drawer: this } }));
  }
  close() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    document.body.classList.remove('drawer-open');
    if (this.releaseFocus) this.releaseFocus();
    if (this.opener && this.opener.focus) this.opener.focus();
    setTimeout(() => this.setAttribute('hidden', ''), 300);
  }
}
customElements.define('drawer-dialog', DrawerDialog);

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-drawer-open]');
  if (!opener) return;
  const drawer = document.getElementById(opener.dataset.drawerOpen);
  if (drawer) {
    e.preventDefault();
    drawer.open(opener);
  }
});

/* ---------- Announcement-Rotator ---------- */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.announcement-bar__slide'));
    if (this.slides.length <= 1) return;
    this.index = 0;
    this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.go(-1));
    this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.go(1));
    if (this.dataset.autoRotate === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.timer = setInterval(() => this.go(1), (parseInt(this.dataset.speed, 10) || 5) * 1000);
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
    }
  }
  disconnectedCallback() { clearInterval(this.timer); }
  go(dir) {
    this.slides[this.index].classList.remove('is-active');
    this.index = (this.index + dir + this.slides.length) % this.slides.length;
    this.slides[this.index].classList.add('is-active');
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* ---------- Sticky Header ---------- */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('.site-header') || this;
    this.onScroll = () => {
      this.header.classList.toggle('is-scrolled', window.scrollY > 4);
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }
  disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
}
customElements.define('sticky-header', StickyHeader);

/* ---------- Desktop-Navigation (Details-Disclosure) ---------- */
class HeaderMenu extends HTMLElement {
  connectedCallback() {
    this.details = Array.from(this.querySelectorAll('details'));
    document.addEventListener('click', (e) => {
      this.details.forEach((d) => {
        if (d.open && !d.contains(e.target)) d.removeAttribute('open');
      });
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.details.forEach((d) => {
          if (d.open) {
            d.removeAttribute('open');
            d.querySelector('summary')?.focus();
          }
        });
      }
    });
    this.details.forEach((d) => {
      d.addEventListener('toggle', () => {
        if (d.open) this.details.forEach((other) => { if (other !== d) other.removeAttribute('open'); });
      });
      d.addEventListener('focusout', (e) => {
        requestAnimationFrame(() => {
          if (d.open && !d.contains(document.activeElement)) d.removeAttribute('open');
        });
      });
    });
  }
}
customElements.define('header-menu', HeaderMenu);

/* ---------- Suche (Modal + Predictive) ---------- */
class SearchModal extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    this.querySelectorAll('[data-search-close]').forEach((btn) =>
      btn.addEventListener('click', () => this.close())
    );
    this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    if (this.input && this.results) {
      this.input.addEventListener('input', debounce(() => this.search(), 300));
    }
  }
  open(opener) {
    this.opener = opener;
    this.classList.add('is-open');
    this.removeAttribute('hidden');
    document.body.classList.add('drawer-open');
    this.releaseFocus = trapFocus(this.querySelector('.search-modal__panel'), this.input);
  }
  close() {
    this.classList.remove('is-open');
    this.setAttribute('hidden', '');
    document.body.classList.remove('drawer-open');
    if (this.releaseFocus) this.releaseFocus();
    if (this.opener) this.opener.focus();
  }
  async search() {
    const query = this.input.value.trim();
    if (query.length < 2) { this.results.innerHTML = ''; return; }
    try {
      const url = `${window.routes.predictive_search_url}.json?q=${encodeURIComponent(query)}&resources[type]=product,article&resources[limit]=6&resources[options][unavailable_products]=last`;
      const response = await fetch(url);
      if (!response.ok) return;
      const data = await response.json();
      this.render(data.resources.results, query);
    } catch (e) { /* Netzwerkfehler still ignorieren */ }
  }
  render(results, query) {
    const products = results.products || [];
    const articles = results.articles || [];
    let html = '';
    if (products.length) {
      html += `<p class="predictive-results__group-title">${this.dataset.labelProducts}</p><ul class="predictive-results__list" role="list">`;
      html += products.map((p) => `
        <li class="predictive-results__item">
          <a href="${p.url}">
            ${p.featured_image ? `<img class="predictive-results__img" src="${p.featured_image.url}&width=104" alt="" width="52" height="52" loading="lazy">` : '<span class="predictive-results__img" aria-hidden="true"></span>'}
            <span>
              <span class="predictive-results__title">${p.title}</span><br>
              <span class="predictive-results__price">${formatMoney(Math.round(parseFloat(p.price) * 100))}</span>
            </span>
          </a>
        </li>`).join('');
      html += '</ul>';
    }
    if (articles.length) {
      html += `<p class="predictive-results__group-title">${this.dataset.labelArticles}</p><ul class="predictive-results__list" role="list">`;
      html += articles.map((a) => `
        <li class="predictive-results__item"><a href="${a.url}"><span class="predictive-results__title">${a.title}</span></a></li>`).join('');
      html += '</ul>';
    }
    if (!html) {
      html = `<p class="text-muted" style="padding:1rem 0.5rem 0.25rem">${this.dataset.labelEmpty}</p>`;
    } else {
      html += `<a class="predictive-results__all" href="${window.routes.search_url}?q=${encodeURIComponent(query)}">${this.dataset.labelAll} „${query}“</a>`;
    }
    this.results.innerHTML = html;
  }
}
customElements.define('search-modal', SearchModal);

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-search-open]');
  if (!opener) return;
  e.preventDefault();
  document.querySelector('search-modal')?.open(opener);
});

/* ---------- Cart Drawer ---------- */
class CartDrawer extends DrawerDialog {}
customElements.define('cart-drawer', CartDrawer);

document.addEventListener('cart:updated', () => {
  const drawer = document.getElementById('CartDrawer');
  if (drawer && window.themeSettings.cartType === 'drawer' && !drawer.classList.contains('is-open')) {
    drawer.open();
  }
});

/* Zeilen im Drawer / auf der Cart-Seite ändern */
document.addEventListener('click', async (e) => {
  const removeBtn = e.target.closest('[data-cart-remove]');
  if (removeBtn) {
    e.preventDefault();
    removeBtn.disabled = true;
    try {
      await CartAPI.change({ line: parseInt(removeBtn.dataset.cartRemove, 10), quantity: 0 });
    } catch (err) { showToast(err.message); }
  }
});
document.addEventListener('change', async (e) => {
  const input = e.target.closest('[data-cart-line-qty]');
  if (!input) return;
  try {
    await CartAPI.change({ line: parseInt(input.dataset.cartLineQty, 10), quantity: parseInt(input.value, 10) });
  } catch (err) {
    showToast(err.message);
    CartAPI.refreshCount();
  }
});

/* ---------- Quantity Input ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const previous = this.input.value;
        if (button.name === 'plus') this.input.stepUp();
        else this.input.stepDown();
        if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      })
    );
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- Product Form (AJAX Add-to-Cart) ---------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.submitButton = this.form.querySelector('[type="submit"]');
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
  }
  async onSubmit(e) {
    e.preventDefault();
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
    const formData = new FormData(this.form);
    const body = { items: [{ id: parseInt(formData.get('id'), 10), quantity: parseInt(formData.get('quantity') || 1, 10) }] };
    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) body.items[0].selling_plan = parseInt(sellingPlan, 10);
    const props = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) props[key.slice(11, -1)] = value;
    }
    if (Object.keys(props).length) body.items[0].properties = props;

    const label = this.submitButton.querySelector('[data-atc-text]') || this.submitButton;
    const originalText = label.textContent;
    this.submitButton.setAttribute('aria-disabled', 'true');
    label.textContent = window.themeStrings.loading;
    try {
      await CartAPI.add(body);
      if (window.themeSettings.cartType !== 'drawer') {
        window.location.href = window.routes.cart_url;
        return;
      }
    } catch (err) {
      showToast(err.message);
    } finally {
      this.submitButton.removeAttribute('aria-disabled');
      label.textContent = originalText;
    }
  }
}
customElements.define('product-form', ProductForm);

/* ---------- Slider (scroll-snap + Buttons) ---------- */
class SliderComponent extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('.slider');
    this.prev = this.querySelector('[data-slider-prev]');
    this.next = this.querySelector('[data-slider-next]');
    if (!this.slider || !this.prev) return;
    this.prev.addEventListener('click', () => this.scrollBySlide(-1));
    this.next.addEventListener('click', () => this.scrollBySlide(1));
    this.slider.addEventListener('scroll', debounce(() => this.updateButtons(), 100), { passive: true });
    this.updateButtons();
  }
  scrollBySlide(dir) {
    const slide = this.slider.firstElementChild;
    if (!slide) return;
    const gap = parseFloat(getComputedStyle(this.slider).columnGap) || 16;
    this.slider.scrollBy({ left: dir * (slide.getBoundingClientRect().width + gap), behavior: 'smooth' });
  }
  updateButtons() {
    const max = this.slider.scrollWidth - this.slider.clientWidth - 4;
    this.prev.toggleAttribute('disabled', this.slider.scrollLeft <= 4);
    this.next.toggleAttribute('disabled', this.slider.scrollLeft >= max);
  }
}
customElements.define('slider-component', SliderComponent);

/* ---------- Bestellnotiz auf der Warenkorbseite ---------- */
document.addEventListener('change', async (e) => {
  const note = e.target.closest('[data-cart-note]');
  if (!note) return;
  try {
    await fetch(`${window.routes.cart_update_url}.js`, {
      ...fetchConfig(),
      body: JSON.stringify({ note: note.value })
    });
  } catch (err) { /* Notiz wird beim Checkout erneut gesendet */ }
});

/* ---------- Such-Tabs (Ergebnisseite) ---------- */
class SearchTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const tab = e.target.closest('[data-tab]');
      if (!tab) return;
      this.querySelectorAll('[data-tab]').forEach((t) => t.setAttribute('aria-selected', t === tab ? 'true' : 'false'));
      const filter = tab.dataset.tab;
      this.querySelectorAll('[data-result-type]').forEach((item) => {
        item.style.display = filter === 'all' || item.dataset.resultType === filter ? '' : 'none';
      });
    });
  }
}
customElements.define('search-tabs', SearchTabs);

/* ---------- Scroll-Animationen ----------
   Persistenter Observer + MutationObserver, damit auch dynamisch nachgeladene
   Elemente (Empfehlungen, „Mehr laden“, Filter-Ergebnisse) sichtbar werden. */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const animateObserver = reducedMotion ? null : new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      entry.target.classList.add('is-inview');
      animateObserver.unobserve(entry.target);
    }
  });
}, { rootMargin: '0px 0px -8% 0px' });

function initScrollAnimations() {
  document.querySelectorAll('[data-animate]:not(.is-inview)').forEach((el) => {
    if (reducedMotion) el.classList.add('is-inview');
    else animateObserver.observe(el);
  });
}
initScrollAnimations();
document.addEventListener('shopify:section:load', initScrollAnimations);
new MutationObserver(() => initScrollAnimations()).observe(document.body, { childList: true, subtree: true });

/* ---------- Generischer 1-Klick-Add-Button ---------- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-add-variant]');
  if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
  e.preventDefault();
  btn.setAttribute('aria-disabled', 'true');
  try {
    await CartAPI.add({ items: [{ id: parseInt(btn.dataset.addVariant, 10), quantity: 1 }] });
  } catch (err) {
    showToast(err.message);
  } finally {
    btn.removeAttribute('aria-disabled');
  }
});

/* ---------- Cross-Sell im Warenkorb (Recommendations API) ---------- */
class CartCrosssell extends HTMLElement {
  connectedCallback() {
    this.load();
  }
  async load() {
    const productId = this.dataset.productId;
    if (!productId) { this.closest('.cart-crosssell')?.remove(); return; }
    try {
      const url = `${window.routes.product_recommendations_url || '/recommendations/products'}.json?product_id=${productId}&limit=${this.dataset.limit || 3}&intent=related`;
      const response = await fetch(url);
      if (!response.ok) throw new Error();
      const data = await response.json();
      const products = (data.products || []).filter((p) => p.available);
      if (!products.length) { this.closest('.cart-crosssell')?.remove(); return; }
      const list = this.querySelector('[data-crosssell-list]');
      list.innerHTML = products.map((p) => `
        <li class="cart-crosssell__item">
          ${p.featured_image ? `<img src="${p.featured_image}&width=112" alt="" width="56" height="56" loading="lazy">` : '<span aria-hidden="true"></span>'}
          <div>
            <a class="cart-crosssell__name" href="${p.url}">${p.title}</a>
            <div class="cart-crosssell__price">${formatMoney(p.price)}</div>
          </div>
          <button type="button" class="button button--small button--secondary" data-add-variant="${p.variants[0].id}" aria-label="${p.title}">+</button>
        </li>`).join('');
    } catch (e) {
      this.closest('.cart-crosssell')?.remove();
    }
  }
}
customElements.define('cart-crosssell', CartCrosssell);

/* ---------- Quick-Add (Varianten-Modal auf Produktkarten) ---------- */
class QuickAdd extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-quick-add-trigger]');
    if (!this.trigger) return;
    this.trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.openModal();
    });
  }
  get variants() {
    try {
      return JSON.parse(this.querySelector('[data-quick-add-variants]').textContent);
    } catch (e) { return []; }
  }
  openModal() {
    let modal = document.getElementById('QuickAddModal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'QuickAddModal';
      modal.className = 'search-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      document.body.appendChild(modal);
    }
    const variants = this.variants;
    modal.innerHTML = `
      <button type="button" class="search-modal__overlay" data-quick-add-close aria-label="${window.themeStrings.close || 'Schließen'}"></button>
      <div class="search-modal__panel">
        <div class="drawer__header" style="padding:0 0 0.75rem; border:0;">
          <h2 class="drawer__title" id="QuickAddModalTitle">${this.dataset.productTitle}</h2>
          <button type="button" class="drawer__close" data-quick-add-close aria-label="${window.themeStrings.close || 'Schließen'}">
            <svg class="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>
          </button>
        </div>
        <div class="purchase-options" role="list">
          ${variants.map((v) => `
            <button type="button" class="purchase-option" style="text-align:left;${v.available ? '' : 'opacity:0.5;'}" data-quick-add-variant="${v.id}" ${v.available ? '' : 'disabled'}>
              <span class="purchase-option__head">
                <span>${v.title}</span>
                <span class="purchase-option__save">${formatMoney(v.price)}${v.unit_price ? ` <small class="text-muted">(${formatMoney(v.unit_price)}/${v.unit_ref || ''})</small>` : ''}</span>
              </span>
            </button>`).join('')}
        </div>
        <p style="margin:1rem 0 0"><a href="${this.dataset.productUrl}">${window.themeStrings.viewDetails || ''}</a></p>
      </div>`;
    modal.classList.add('is-open');
    modal.setAttribute('aria-labelledby', 'QuickAddModalTitle');
    document.body.classList.add('drawer-open');
    const close = () => {
      modal.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      if (this._release) this._release();
      this.trigger.focus();
    };
    modal.querySelectorAll('[data-quick-add-close]').forEach((btn) => btn.addEventListener('click', close));
    modal.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    modal.querySelectorAll('[data-quick-add-variant]').forEach((btn) =>
      btn.addEventListener('click', async () => {
        btn.setAttribute('aria-disabled', 'true');
        try {
          await CartAPI.add({ items: [{ id: parseInt(btn.dataset.quickAddVariant, 10), quantity: 1 }] });
          close();
        } catch (err) {
          showToast(err.message);
          btn.removeAttribute('aria-disabled');
        }
      })
    );
    this._release = trapFocus(modal.querySelector('.search-modal__panel'));
  }
}
customElements.define('quick-add', QuickAdd);

/* ---------- Recently Viewed (sessionStorage, kein localStorage) ---------- */
export const RecentlyViewed = {
  key: 'schnauze:recently-viewed',
  get() {
    try { return JSON.parse(sessionStorage.getItem(this.key)) || []; } catch (e) { return []; }
  },
  add(product) {
    try {
      const list = this.get().filter((p) => p.handle !== product.handle);
      list.unshift(product);
      sessionStorage.setItem(this.key, JSON.stringify(list.slice(0, 10)));
    } catch (e) { /* Storage nicht verfügbar – Kernfunktion bleibt intakt */ }
  }
};
window.RecentlyViewed = RecentlyViewed;
